import { Store } from 'react-notifications-component';

// Deduplication store to prevent duplicate notifications (e.g., from both WebSocket and FCM)
const recentNotifications = new Map<string, number>();

export const isDuplicateNotification = (key: string, ttlMs = 4000): boolean => {
	const now = Date.now();
	const lastTime = recentNotifications.get(key);
	if (lastTime && now - lastTime < ttlMs) {
		return true;
	}
	recentNotifications.set(key, now);

	// Periodic cleanup
	if (recentNotifications.size > 50) {
		recentNotifications.forEach((time, k) => {
			if (now - time > 10000) {
				recentNotifications.delete(k);
			}
		});
	}
	return false;
};

export type TNotificationType = 'info' | 'success' | 'warning' | 'danger' | 'default';

export type TNotificationPosition =
	| 'top-right'
	| 'top-left'
	| 'top-center'
	| 'bottom-right'
	| 'bottom-left'
	| 'bottom-center';

const showNotification = (
	title: string | JSX.Element,
	message?: string | JSX.Element,
	type: TNotificationType | string = 'default',
	onClick?: () => void,
	position: TNotificationPosition = 'top-right',
) => {
	const finalTitle = title;
	const finalMessage = message !== undefined && message !== null ? message : '';

	let resolvedType: 'info' | 'success' | 'warning' | 'danger' | 'default' = 'info';
	const raw = String(type || '').toLowerCase();
	if (raw.includes('success') || raw.includes('complete') || raw.includes('approved')) {
		resolvedType = 'success';
	} else if (raw.includes('warn') || raw.includes('pending')) {
		resolvedType = 'warning';
	} else if (raw.includes('danger') || raw.includes('error') || raw.includes('reject')) {
		resolvedType = 'danger';
	} else if (type === 'default') {
		resolvedType = 'default';
	}

	const isBottom = position.startsWith('bottom');

	Store.addNotification({
		title: finalTitle,
		message: finalMessage,
		// @ts-ignore
		type: resolvedType,
		insert: isBottom ? 'bottom' : 'top',
		container: position,
		animationIn: isBottom
			? ['animate__animated', 'animate__fadeInUp']
			: ['animate__animated', 'animate__fadeInRight'],
		animationOut: isBottom
			? ['animate__animated', 'animate__fadeOutDown']
			: ['animate__animated', 'animate__fadeOutRight'],
		dismiss: {
			duration: 5000,
			pauseOnHover: true,
			onScreen: true,
			showIcon: true,
			waitForAnimation: true,
			click: true,
			touch: true,
		},
		// IMPORTANT: Only trigger onClick when removal was caused by an active user click or touch.
		// react-notifications-component passes removalFlag: 'timeout' | 'click' | 'touch' | 'manual'.
		// If removalFlag is 'timeout' (auto-dismiss after duration), DO NOT execute onClick!
		onRemoval: (_id: string, removedBy?: string) => {
			if (onClick && (removedBy === 'click' || removedBy === 'touch')) {
				onClick();
			}
		},
	});
};

export default showNotification;
